import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
  Autocomplete,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import { transactionService, sparePartService, machineService } from '../services';
import { format } from 'date-fns';

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [parts, setParts] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<number | null>(null);
  // filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [formData, setFormData] = useState({
    transaction_type: 'out',
    spare_part_id: '',
    quantity: 0,
    unit_price: 0,
    reference_number: '',
    from_location: '',
    to_location: '',
    machine_id: '',
    reason: '',
    notes: '',
    store_id: '1',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [transData, partsData, machinesData] = await Promise.all([
        transactionService.getAll(),
        sparePartService.getAll(),
        machineService.getAll(),
      ]);
      setTransactions(transData.transactions);
      setParts(partsData.parts);
      setMachines(machinesData.machines);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleOpenDialog = () => {
    setEditingId(null);
    setFormData({
      transaction_type: 'out',
      spare_part_id: '',
      quantity: 0,
      unit_price: 0,
      reference_number: '',
      from_location: '',
      to_location: '',
      machine_id: '',
      reason: '',
      notes: '',
      store_id: '1',
    });
    setDialogOpen(true);
  };

  const handleEdit = (transaction: any) => {
    setEditingId(transaction.id);
    setFormData({
      transaction_type: transaction.transaction_type,
      spare_part_id: String(transaction.spare_part_id),
      quantity: transaction.quantity,
      unit_price: transaction.unit_price || 0,
      reference_number: transaction.reference_number || '',
      from_location: transaction.from_location || '',
      to_location: transaction.to_location || '',
      machine_id: transaction.machine_id ? String(transaction.machine_id) : '',
      reason: transaction.reason || '',
      notes: transaction.notes || '',
      store_id: transaction.store_id ? String(transaction.store_id) : '1',
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setTransactionToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (transactionToDelete === null) return;
    
    try {
      await transactionService.delete(transactionToDelete);
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      alert('Failed to delete transaction. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        spare_part_id: Number(formData.spare_part_id),
        machine_id: formData.machine_id ? Number(formData.machine_id) : undefined,
        store_id: formData.store_id ? Number(formData.store_id) : undefined,
      };
      
      if (editingId !== null) {
        await transactionService.update(editingId, data);
      } else {
        await transactionService.create(data);
      }
      
      setDialogOpen(false);
      setEditingId(null);
      loadData();
    } catch (error) {
      console.error('Failed to save transaction:', error);
      alert('Failed to save transaction. Please try again.');
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'in':
        return 'success';
      case 'out':
        return 'error';
      case 'adjustment':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    const matchesSearch =
      !searchTerm ||
      (t.part_name && t.part_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.part_number && t.part_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.reference_number && t.reference_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesMachine =
      !machineFilter ||
      (t.machine_id && t.machine_id.toString() === machineFilter);
    const matchesType = !typeFilter || t.transaction_type === typeFilter;
    return matchesSearch && matchesMachine && matchesType;
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transactions</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
          New Transaction
        </Button>
      </Box>

      {/* Filter bar */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search by part, part number or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Machine"
          value={machineFilter}
          onChange={(e) => setMachineFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Machines</MenuItem>
          {machines.map((m) => (
            <MenuItem key={m.id} value={m.id.toString()}>
              {m.machine_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="in">Stock IN</MenuItem>
          <MenuItem value="out">Stock OUT</MenuItem>
          <MenuItem value="adjustment">Adjustment</MenuItem>
        </TextField>
        {(searchTerm || machineFilter || typeFilter) && (
          <Button
            size="small"
            onClick={() => { setSearchTerm(''); setMachineFilter(''); setTypeFilter(''); }}
          >
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="textSecondary" ml="auto">
          {filteredTransactions.length} of {transactions.length} transactions
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Part</TableCell>
              <TableCell>Machine</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>
                  {format(new Date(transaction.transaction_date), 'MMM dd, yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.transaction_type.toUpperCase()}
                    size="small"
                    color={getTransactionColor(transaction.transaction_type)}
                  />
                </TableCell>
                <TableCell>
                  {transaction.store_id === 2 || transaction.store_id === '2' ? 'EMS' : 'PDS'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{transaction.part_name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {transaction.part_number}
                  </Typography>
                </TableCell>
                <TableCell>{transaction.machine_name || '-'}</TableCell>
                <TableCell align="right">{transaction.quantity}</TableCell>
                <TableCell>{transaction.reference_number || '-'}</TableCell>
                <TableCell>{transaction.user_name || '-'}</TableCell>
                <TableCell>{transaction.reason || '-'}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit transaction">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(transaction)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete transaction">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(transaction.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredTransactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary">
                    {transactions.length === 0 ? 'No transactions found' : 'No transactions match the current filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId !== null ? 'Edit Transaction' : 'New Transaction'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Transaction Type"
                value={formData.transaction_type}
                onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
              >
                <MenuItem value="out">Stock OUT</MenuItem>
                <MenuItem value="in">Stock IN</MenuItem>
                <MenuItem value="adjustment">Adjustment</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                required
                label="Stock Location"
                value={formData.store_id}
                onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                helperText="The selected stock location's balance will be updated by this transaction."
              >
                <MenuItem value="1">PDS - Printing Department Stock</MenuItem>
                <MenuItem value="2">EMS - Engineering and Maintenance Stock</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={parts}
                value={parts.find((p) => String(p.id) === String(formData.spare_part_id)) || null}
                onChange={(_, part) => {
                  setFormData({ ...formData, spare_part_id: part ? String(part.id) : '' });
                }}
                filterOptions={(options, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((part) => {
                    const haystack = [
                      part.part_name,
                      part.part_number,
                      part.description,
                      part.location,
                      part.machine_names,
                      String(part.current_stock),
                      String(part.pds_stock),
                      String(part.ems_stock),
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase();
                    return haystack.includes(q);
                  });
                }}
                getOptionLabel={(part) => `${part.part_name || ''} (${part.part_number || ''})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Spare Part"
                    placeholder="Search by name, part number, description, stock, location or machine"
                    required
                  />
                )}
                renderOption={(props, part) => (
                  <Box component="li" {...props} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={`${part.part_name || '-'} (${part.part_number || '-'})`}
                      secondary={
                        <>
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            Description: {part.description || '-'}
                          </Typography>
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            Total stock: {part.current_stock ?? 0} | In stock: PDS {part.pds_stock ?? 0}, EMS {part.ems_stock ?? 0}
                          </Typography>
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            Location: {part.location || '-'} | Machines: {part.machine_names || '-'}
                          </Typography>
                        </>
                      }
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              />
            </Grid>

            {formData.transaction_type === 'out' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Machine (Optional)"
                  value={formData.machine_id}
                  onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                  helperText="Machine this part was used on"
                >
                  <MenuItem value="">None</MenuItem>
                  {machines.map((machine) => (
                    <MenuItem key={machine.id} value={machine.id}>
                      {machine.machine_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {formData.transaction_type === 'in' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="To Location"
                  value={formData.to_location}
                  onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                />
              </Grid>
            )}

            {formData.transaction_type === 'out' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="From Location"
                  value={formData.from_location}
                  onChange={(e) => setFormData({ ...formData, from_location: e.target.value })}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingId !== null ? 'Update Transaction' : 'Create Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this transaction? This will reverse the stock changes made by this transaction.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transactions;
